import { ArrowBack } from '@mui/icons-material';
import { Box, Button, CircularProgress, Divider, Stack, type Theme, useMediaQuery } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { createElement, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { BOTTOM_BAR_HEIGHT } from '/@/components/app-navbar/bottombar';
import PageContainerWithSidebar from '/@/components/bayer/page-container-with-sidebar';
import { _ } from '/@/locale';
import { logout } from '/@/store/session/actions';
import { reverse } from '/@/utils/router';
import ProfileHeader from './components/profile-header';
import ProfileTabs from './components/profile-tabs';
import { type TabKey, useSidebarTabs, useTabs } from './tabs';
import type { LanguageCountryFormData } from './tabs/language-and-country-edit';
import type { PasswordFormData } from './tabs/password-edit';
import type { ProfileFormData } from './tabs/profile-edit';
import type { UnitsFormData } from './tabs/units-edit';

const Sidebar = ({
    tabs,
    selectedTab,
    setSelectedTab,
}: {
    tabs: ReturnType<typeof useSidebarTabs>;
    selectedTab?: TabKey;
    setSelectedTab: (tab: TabKey) => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return (
        <Stack divider={<Divider />} sx={{ height: '100%' }}>
            <ProfileHeader />
            {tabs.map((tab) => (
                <ProfileTabs
                    key={tab.title}
                    title={tab.title}
                    tabs={tab.tabs}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                />
            ))}
            <Stack py={2.5} pl={5} pr={2}>
                <Button
                    variant='text'
                    color='primary'
                    sx={{ width: 'fit-content' }}
                    onClick={() => {
                        dispatch(logout());
                        navigate(reverse('NewHome'));
                    }}
                >
                    {_('Cerrar sesión')}
                </Button>
            </Stack>
            <Box />
        </Stack>
    );
};

export type ProfileV2FormData = LanguageCountryFormData & ProfileFormData & PasswordFormData & UnitsFormData;

const ProfileV2 = () => {
    const [mobilePage, setMobilePage] = useState<'menu' | 'view'>('menu');
    const [selectedTab, setSelectedTab] = useState<TabKey>('profileEdit');
    const tabs = useSidebarTabs();
    const tabsContent = useTabs();
    const activeTab = tabsContent[selectedTab];
    const [activeTabRef, setActiveTabRef] = useState<typeof activeTab | null>(null);
    const form = useForm<ProfileV2FormData>({
        defaultValues: {
            language: '',
            country: '',
            //
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            type: '',
            subtype: '',
            receiveNotifications: false,
            receiveWhatsappNotifications: false,
            //
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            //
            system: '',
            area: '',
            cornYield: '',
            cornDensity: '',
            soyYield: '',
            soyDensity: '',
        },
        mode: 'onChange',
    });

    const onSubmit = useCallback(async () => {
        if (!activeTabRef) return;

        try {
            await form.handleSubmit(async (data) => {
                await activeTabRef.onSubmit(data);
                enqueueSnackbar({ message: _('Cambios guardados con éxito'), variant: 'success' });
            })();
        } catch {
            // Error is handled by the form
        }
    }, [form, activeTabRef]);

    const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    if (isXs)
        return (
            <Stack sx={{ height: '100%' }}>
                {mobilePage === 'menu' ? (
                    <Sidebar
                        tabs={tabs}
                        setSelectedTab={(tab) => {
                            setSelectedTab(tab);
                            setMobilePage('view');
                        }}
                    />
                ) : (
                    <Stack
                        px={2}
                        py={3}
                        spacing={2}
                        sx={{
                            position: 'relative',
                        }}
                    >
                        <Button
                            variant='text'
                            onClick={() => setMobilePage('menu')}
                            startIcon={<ArrowBack color='primary.onSurface' />}
                            color='primary'
                            sx={{ width: 'fit-content' }}
                        >
                            {_('Atrás')}
                        </Button>

                        {createElement(activeTab, { ref: setActiveTabRef, form })}
                        {activeTabRef?.onSubmit && (
                            <Stack
                                px={1}
                                py={2}
                                sx={{
                                    width: '100%',
                                    borderTop: (theme: Theme) => `1px solid ${theme.palette.divider}`,
                                    position: 'fixed',
                                    bottom: BOTTOM_BAR_HEIGHT,
                                    left: 0,
                                    background: 'white',
                                    zIndex: 1000,
                                }}
                            >
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    onClick={onSubmit}
                                    disabled={!form?.formState.isDirty || form?.formState.isSubmitting}
                                    startIcon={
                                        form?.formState.isSubmitting ? <CircularProgress size={20} /> : undefined
                                    }
                                >
                                    {_('Guardar')}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        );
    return (
        <PageContainerWithSidebar
            sidebar={<Sidebar tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />}
            sidebarDivider={false}
            actions={
                <Stack
                    p={3}
                    sx={{ width: '100%', borderTop: (theme: Theme) => `1px solid ${theme.palette.divider}` }}
                    alignItems='flex-end'
                >
                    {activeTabRef?.onSubmit ? (
                        <Button
                            variant='contained'
                            color='secondary'
                            sx={{ width: 'fit-content' }}
                            onClick={onSubmit}
                            disabled={!form?.formState.isDirty || form?.formState.isSubmitting}
                            startIcon={form?.formState.isSubmitting ? <CircularProgress size={20} /> : undefined}
                        >
                            {_('Guardar')}
                        </Button>
                    ) : undefined}
                </Stack>
            }
        >
            {createElement(activeTab, { ref: setActiveTabRef, form })}
        </PageContainerWithSidebar>
    );
};

export default ProfileV2;
